import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { SharedLayout } from '../components/layout/SharedLayout';
import { theme } from '../styles/theme';

const HeroSection = styled.section`
  background: #232B3B;
  padding: 100px 20px 80px;
  margin-top: -80px;
  padding-top: 140px;
  color: white;
  text-align: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/650f1c8c6cf62f0b696c3d20_Lake2.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.15;
    z-index: 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const PageTitle = styled.h1`
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 800;
  margin-bottom: 20px;
  letter-spacing: -1px;
`;

const PageSubtitle = styled.p`
  font-size: clamp(18px, 2.5vw, 24px);
  opacity: 0.9;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.5;
`;

const ToolsSection = styled.section`
  padding: 80px 20px;
  background: #f8f9fa;
`;

const FeaturedTools = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-bottom: 80px;
`;

const FeaturedCard = styled.div`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  }
`;

const CardHeader = styled.div<{ color: string }>`
  background: ${props => props.color};
  padding: 30px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  }
`;

const ToolIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: white;
    opacity: 0.2;
    border-radius: 16px;
    transform: rotate(45deg);
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 60%;
    height: 60%;
    background: white;
    opacity: 0.3;
    border-radius: 12px;
    top: 20%;
    left: 20%;
  }
`;

const ToolBadge = styled.div`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 5px 12px;
  border-radius: 15px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const CardBody = styled.div`
  padding: 40px;
`;

const ToolTitle = styled.h3`
  font-size: 28px;
  font-weight: 800;
  color: #333;
  margin-bottom: 15px;
`;

const ToolDescription = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 25px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 30px;
`;

const Feature = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
  color: #555;
  
  &::before {
    content: '•';
    color: #4CAF50;
    font-weight: bold;
    margin-right: 10px;
    font-size: 20px;
  }
`;

const ToolCTA = styled.a`
  display: inline-block;
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  color: white;
  padding: 16px 32px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: bold;
  font-size: 16px;
  text-align: center;
  transition: all 0.3s;
  margin-right: 16px;
  margin-bottom: 10px;
  
  &:hover {
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(35, 43, 59, 0.3);
    background: linear-gradient(135deg, #1a1f2e 0%, #232B3B 100%);
  }
  
  &:last-child {
    margin-right: 0;
  }
`;

const SecondaryLink = styled.a`
  display: inline-block;
  color: #4a90e2;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  padding: 16px 0;
  transition: all 0.3s;
  margin-bottom: 10px;
  
  &:hover {
    color: #2c5aa0;
    text-decoration: underline;
  }
  
  &::after {
    content: '→';
    margin-left: 5px;
  }
`;

const CardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
`;

const AdditionalTools = styled.div`
  margin-top: 60px;
`;

const SectionHeader = styled.h2`
  font-size: 36px;
  font-weight: 800;
  color: #333;
  text-align: center;
  margin-bottom: 50px;
`;

const ToolGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 25px;
`;

const ToolCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.06);
  transition: all 0.3s;
  text-align: center;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ToolCardIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  border-radius: 12px;
  margin: 0 auto 20px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 30px;
    height: 30px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const ToolCardTitle = styled.h4`
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 12px;
`;

const ToolCardDescription = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin-bottom: 24px;
`;

const ToolCardLink = styled.a`
  color: #232B3B;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  transition: all 0.3s;
  
  &:hover {
    color: #ffd700;
    
    &::after {
      transform: translateX(5px);
    }
  }
  
  &::after {
    content: '→';
    margin-left: 5px;
    transition: transform 0.3s;
  }
`;

const ComingSoonBadge = styled.div`
  display: inline-block;
  background: #e9ecef;
  color: #6c757d;
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: bold;
  margin-top: 8px;
`;

const InfoSection = styled.section`
  background: white;
  padding: 80px 20px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  max-width: 900px;
  margin: 0 auto;
`;

const InfoCard = styled.div`
  text-align: center;
`;

const InfoIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 16px;
  margin: 0 auto 20px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
    border-radius: 8px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const InfoTitle = styled.h3`
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.5;
`;

const CTAContainer = styled.div`
  text-align: center;
  margin-top: 60px;
`;

const CTAText = styled.p`
  font-size: 18px;
  color: #666;
  margin-bottom: 24px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const ConsultButton = styled(Link)`
  display: inline-block;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #232B3B;
  padding: 18px 40px;
  border-radius: 8px;
  font-weight: bold;
  font-size: 18px;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(255, 215, 0, 0.4);
  }
`;

export const Tools: React.FC = () => {
  return (
    <SharedLayout>
      <HeroSection>
        <Container>
          <PageTitle>Powerful Tools for Smarter Decisions</PageTitle>
          <PageSubtitle>
            Free, AI-powered calculators and tools to analyze your retirement options with confidence
          </PageSubtitle>
        </Container>
      </HeroSection>

      <ToolsSection>
        <Container>
          <FeaturedTools>
            <FeaturedCard>
              <CardHeader color="linear-gradient(135deg, #10a37f 0%, #13c89f 100%)">
                <ToolIcon />
                <ToolBadge>Quick & Free</ToolBadge>
              </CardHeader>
              <CardBody>
                <ToolTitle>Get Quick Answer</ToolTitle>
                <ToolDescription>
                  Get instant expert feedback on your annuity questions. Our team responds within 24 hours 
                  with personalized guidance tailored to your specific situation.
                </ToolDescription>
                <FeatureList>
                  <Feature>24-hour response guarantee</Feature>
                  <Feature>Expert analysis from 40+ years of experience</Feature>
                  <Feature>100% confidential and secure</Feature>
                  <Feature>Always free with no obligations</Feature>
                  <Feature>Data-driven answers, not sales pitches</Feature>
                </FeatureList>
                <CardActions>
                  <ToolCTA href="/quick-answer">
                    Get Quick Answer →
                  </ToolCTA>
                </CardActions>
              </CardBody>
            </FeaturedCard>

            <FeaturedCard>
              <CardHeader color="linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)">
                <ToolIcon />
                <ToolBadge>Comprehensive</ToolBadge>
              </CardHeader>
              <CardBody>
                <ToolTitle>Deep Dive Analysis</ToolTitle>
                <ToolDescription>
                  Compare up to 10 annuities with real market data. Perfect for agents 
                  or anyone who needs comprehensive analysis of multiple products.
                </ToolDescription>
                <FeatureList>
                  <Feature>Real daily market data since index inception</Feature>
                  <Feature>Permutation testing across all possible purchase dates</Feature>
                  <Feature>Actual cap and participation rate changes over time</Feature>
                  <Feature>Comprehensive analysis of multiple products</Feature>
                  <Feature>40 years of expertise built into every analysis</Feature>
                </FeatureList>
                <CardActions>
                  <ToolCTA href="/deep-dive">
                    Start Deep Dive →
                  </ToolCTA>
                </CardActions>
              </CardBody>
            </FeaturedCard>
          </FeaturedTools>

          <AdditionalTools>
            <SectionHeader>Additional Tools</SectionHeader>
            <ToolGrid>
              <ToolCard>
                <ToolCardIcon />
                <ToolCardTitle>AI Annuity Backtester</ToolCardTitle>
                <ToolCardDescription>
                  Test any annuity against 20+ years of real market data with our advanced backtesting tool.
                </ToolCardDescription>
                <ToolCardLink href="/ai-backtester">
                  Launch Backtester
                </ToolCardLink>
              </ToolCard>
              
              <ToolCard>
                <ToolCardIcon />
                <ToolCardTitle>Retirement Calculator</ToolCardTitle>
                <ToolCardDescription>
                  Plan your retirement with our comprehensive calculator including Social Security optimization.
                </ToolCardDescription>
                <ToolCardLink href="/retirement-calculator">
                  Open Calculator
                </ToolCardLink>
              </ToolCard>
              
              <ToolCard>
                <ToolCardIcon />
                <ToolCardTitle>Annuity Basics</ToolCardTitle>
                <ToolCardDescription>
                  Learn the fundamentals of annuities with our comprehensive educational guide.
                </ToolCardDescription>
                <ToolCardLink href="/annuity-basics">
                  Learn More
                </ToolCardLink>
              </ToolCard>
            </ToolGrid>
          </AdditionalTools>

        </Container>
      </ToolsSection>

      <InfoSection>
        <Container>
          <SectionHeader>Why Our Tools Are Different</SectionHeader>
          <InfoGrid>
            <InfoCard>
              <InfoIcon />
              <InfoTitle>Data-Driven</InfoTitle>
              <InfoText>
                Based on real historical data and actuarial science, not sales projections
              </InfoText>
            </InfoCard>
            <InfoCard>
              <InfoIcon />
              <InfoTitle>Always Free</InfoTitle>
              <InfoText>
                No hidden costs, no email gates, no sales pressure - ever
              </InfoText>
            </InfoCard>
          </InfoGrid>
          <CTAContainer>
            <CTAText>
              Ready to put these tools to work with expert guidance?
            </CTAText>
            <ConsultButton to="/retirevana">
              Schedule Your Free Consultation
            </ConsultButton>
          </CTAContainer>
        </Container>
      </InfoSection>
    </SharedLayout>
  );
};